import React from 'react';

// Own, on-brand SVG "diagrams" used as hero visuals for concept articles.
// Crisp vector graphics (no AI-generated text) so they render perfectly.

const NAVY = '#0A2A63';
const CYAN = '#00B7C7';
const MAGENTA = '#E5007E';
const YELLOW = '#FFD700';
const GREEN = '#0F7B3F';
const ORANGE = '#FF4E50';
const SLATE = '#64748b';

const STANDARD_SWATCHES = [CYAN, MAGENTA, YELLOW, GREEN];
const FILE_SWATCHES_TOP = [CYAN, MAGENTA];
const FILE_SWATCHES_BOTTOM = [YELLOW, GREEN];

const Swatches = ({ x, y, colors, size = 16, gap = 4 }) => (
  <g>
    {colors.map((c, i) => (
      <rect key={c + i} x={x + i * (size + gap)} y={y} width={size} height={size} rx={3} fill={c} />
    ))}
  </g>
);

// 1) Multi-site color standardization
const ColorWorkflowDiagram = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" role="img" aria-label="Multi-site color standardization diagram">
    <defs>
      <linearGradient id="cwBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0A2A63" />
        <stop offset="1" stopColor="#061a3f" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="600" height="360" fill="url(#cwBg)" />
    <text x="30" y="46" fill={ORANGE} fontFamily="Arial, sans-serif" fontSize="12" fontWeight="700" letterSpacing="3">ONE STANDARD</text>

    {/* connectors */}
    <g stroke={CYAN} strokeWidth="2" opacity="0.5" fill="none">
      <path d="M300 150 C 200 150, 150 220, 120 250" />
      <path d="M300 150 C 300 200, 300 220, 300 250" />
      <path d="M300 150 C 400 150, 450 220, 480 250" />
    </g>

    {/* central standard card */}
    <g>
      <rect x="210" y="72" width="180" height="86" rx="14" fill="#ffffff" />
      <rect x="210" y="72" width="180" height="6" rx="3" fill={ORANGE} />
      <text x="230" y="106" fill={NAVY} fontFamily="Arial, sans-serif" fontSize="15" fontWeight="800">Color Standard</text>
      <Swatches x={230} y={120} colors={STANDARD_SWATCHES} />
    </g>

    {/* facility nodes */}
    {[
      { x: 40, label: 'Facility A' },
      { x: 220, label: 'Facility B' },
      { x: 400, label: 'Facility C' },
    ].map((f) => (
      <g key={f.label}>
        <rect x={f.x} y="250" width="160" height="78" rx="12" fill="#ffffff" opacity="0.96" />
        <text x={f.x + 18} y="280" fill={NAVY} fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">{f.label}</text>
        <Swatches x={f.x + 18} y={294} colors={STANDARD_SWATCHES} size={14} />
        <text x={f.x + 128} y="322" fill={GREEN} fontFamily="Arial, sans-serif" fontSize="16" fontWeight="800">✓</text>
      </g>
    ))}
  </svg>
);

// 2) Same file -> different color across devices
const DeviceVarianceDiagram = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" role="img" aria-label="Same file, different color across devices diagram">
    <rect x="0" y="0" width="600" height="360" fill="#f1f5f9" />
    <text x="30" y="42" fill={SLATE} fontFamily="Arial, sans-serif" fontSize="12" fontWeight="700" letterSpacing="2">SAME FILE · DIFFERENT OUTPUT</text>

    {/* file card */}
    <g>
      <rect x="34" y="130" width="104" height="120" rx="12" fill="#ffffff" stroke={NAVY} strokeWidth="2" />
      <path d="M118 130 l0 20 l20 0" fill="none" stroke={NAVY} strokeWidth="2" />
      <Swatches x={52} y={158} colors={FILE_SWATCHES_TOP} size={16} />
      <Swatches x={52} y={180} colors={FILE_SWATCHES_BOTTOM} size={16} />
      <text x="52" y="228" fill={NAVY} fontFamily="Arial, sans-serif" fontSize="12" fontWeight="700">artwork.pdf</text>
    </g>

    {/* arrows */}
    <g stroke={NAVY} strokeWidth="2" fill="none" opacity="0.7">
      <path d="M150 160 L 220 96" markerEnd="url(#arw)" />
      <path d="M150 190 L 220 190" markerEnd="url(#arw)" />
      <path d="M150 220 L 220 284" markerEnd="url(#arw)" />
    </g>
    <defs>
      <marker id="arw" markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto">
        <path d="M0 0 L6 3 L0 6 z" fill={NAVY} />
      </marker>
    </defs>

    {/* device outputs with drifting color */}
    {[
      { y: 60, label: 'Device A', bar: CYAN },
      { y: 154, label: 'Device B', bar: '#0093a0' },
      { y: 248, label: 'Device C', bar: '#66d4de' },
    ].map((d) => (
      <g key={d.label}>
        <rect x="240" y={d.y} width="240" height="76" rx="12" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5" />
        <text x="260" y={d.y + 28} fill={NAVY} fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">{d.label}</text>
        <rect x="260" y={d.y + 40} width="200" height="18" rx="4" fill={d.bar} />
      </g>
    ))}

    {/* variance callout */}
    <g>
      <rect x="498" y="150" width="74" height="60" rx="10" fill={ORANGE} />
      <text x="535" y="180" fill={NAVY} fontFamily="Arial, sans-serif" fontSize="18" fontWeight="800" textAnchor="middle">ΔE</text>
      <text x="535" y="198" fill={NAVY} fontFamily="Arial, sans-serif" fontSize="10" fontWeight="700" textAnchor="middle">varies</text>
    </g>
  </svg>
);

// 3) KPI scorecard
const KPI_TILES = [
  { k: 'Reprint Rate', c: MAGENTA, bars: [10, 16, 12, 20, 14] },
  { k: 'Waste / Job', c: ORANGE, bars: [14, 10, 18, 12, 16] },
  { k: 'Approval Time', c: CYAN, bars: [18, 14, 20, 12, 10] },
  { k: 'Setup Time', c: GREEN, bars: [12, 18, 14, 16, 20] },
  { k: 'Color Fails', c: '#6A0DAD', bars: [16, 12, 10, 18, 14] },
  { k: 'Downtime', c: '#FF6B35', bars: [10, 14, 18, 12, 16] },
  { k: 'Operator', c: NAVY, bars: [14, 20, 12, 16, 10] },
];

const KpiScorecardDiagram = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" role="img" aria-label="Print operations KPI scorecard diagram">
    <rect x="0" y="0" width="600" height="360" fill={NAVY} />
    <text x="30" y="44" fill="#ffffff" fontFamily="Arial, sans-serif" fontSize="17" fontWeight="800">KPI Scorecard</text>
    <text x="30" y="64" fill={ORANGE} fontFamily="Arial, sans-serif" fontSize="11" fontWeight="700" letterSpacing="2">PRINT OPERATIONS</text>
    <rect x="30" y="76" width="540" height="3" rx="1.5" fill={ORANGE} opacity="0.7" />

    {KPI_TILES.map((t, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      const x = 30 + col * 138;
      const y = 96 + row * 122;
      return (
        <g key={t.k}>
          <rect x={x} y={y} width="126" height="106" rx="12" fill="#ffffff" opacity="0.06" stroke="#ffffff" strokeOpacity="0.14" />
          <circle cx={x + 18} cy={y + 24} r="5" fill={t.c} />
          <text x={x + 32} y={y + 28} fill="#ffffff" fontFamily="Arial, sans-serif" fontSize="11.5" fontWeight="700">{t.k}</text>
          {t.bars.map((b, bi) => (
            <rect key={bi} x={x + 16 + bi * 22} y={y + 88 - b} width="14" height={b} rx="3" fill={t.c} opacity={0.55 + bi * 0.09} />
          ))}
          <line x1={x + 14} y1={y + 88} x2={x + 112} y2={y + 88} stroke="#ffffff" strokeOpacity="0.15" strokeWidth="1" />
        </g>
      );
    })}
  </svg>
);

const DIAGRAMS = {
  'color-workflow': ColorWorkflowDiagram,
  'device-variance': DeviceVarianceDiagram,
  'kpi-scorecard': KpiScorecardDiagram,
};

export const BlogHeroVisual = ({ post, className = '', rounded = 'rounded-2xl' }) => {
  if (post?.hero_type === 'diagram') {
    const Diagram = DIAGRAMS[post.hero_diagram] || KpiScorecardDiagram;
    return (
      <div className={`relative overflow-hidden ${rounded} ${className}`} data-testid="blog-hero-diagram">
        <div className="aspect-[5/3] w-full">
          <Diagram />
        </div>
      </div>
    );
  }
  return (
    <div className={`relative overflow-hidden ${rounded} ${className}`} data-testid="blog-hero-photo">
      <div className="aspect-[5/3] w-full">
        <img src={post?.hero_image} alt={post?.title || ''} className="w-full h-full object-cover" />
      </div>
    </div>
  );
};

export default BlogHeroVisual;
